/**
 * Game registry —— **glob 自動註冊 + per-game lazy chunk**（2026-07 重構）。
 *
 * 不再手動 import / 註冊：只要檔案存在就自動入列，且每款遊戲被 Vite 拆成
 * 獨立 chunk，**執行期只載入當前 gameId 那一款**（content + historyBoard +
 * 自訂 modal），其餘 50+ 款不進玩家的下載量。
 *
 * 檔案約定（= 註冊規則，取代舊 CONTENT_REGISTRY / HELP_REGISTRY 手寫表）：
 *   - `src/games/<gameId>/content.ts` export `<UPPER>_CONTENT`
 *       → 該 gameId 視為已註冊；paytable / rules 走共用元件 + content 資料驅動。
 *   - history modal 三選一（依檔案存在自動判定，優先序同舊 registry 慣例）：
 *       1. `<gameId>/components/<Camel>HistoryDetailModal.vue`（default export）
 *            → 完全自訂 modal（多層 overlay / #win-top / #after-win / 自訂排版）。
 *       2. `<gameId>/historyBoard.ts` export `<UPPER>_HISTORY_BOARD`
 *            → 共用 `BoardHistoryDetailModal` 依 HistoryBoardConfig 渲染（自畫盤面）。
 *       3. 都沒有 → 共用 `HistoryDetailModal`（最小骨架）。
 *   （重構時 59 款盤點：45 款走 historyBoard、14 款走自訂 modal、0 款衝突 —— 若同款
 *    同時出現兩種檔案，自訂 modal 優先。）
 *
 * 載入模型：
 *   - resolver（resolveGameContent / resolveGameLocalConfig / resolveHistoryContent /
 *     resolveNativeHelpComponent / resolveHistoryBoardConfig）**維持同步**，讀 reactive
 *     cache —— 既有 consumer（共用 modal、per-game 元件、stores）一行不用改。
 *   - 開機時（useGameId.fetchGameCode / replay 入口）`await ensureGameLoaded(gameId)`
 *     把當款的模組載進 cache；cache 是 reactive，先跑到的 computed 會在載完後自動重算。
 *   - 未註冊的 gameId 行為不變：placeholder 流程（見 ./_placeholder.ts）。
 *
 * 新增遊戲：建 `src/games/<gameId>/content.ts`（+ 需要的話 historyBoard.ts / 自訂
 * modal）**即完成註冊**，不用改本檔、也不用改 nativeHelpGames.ts（白名單改由
 * REGISTERED_GAME_IDS 派生）。`pnpm new:game` 只負責產檔。
 */

import { shallowReactive, type Component } from 'vue'

import type { GameContent, GameHistoryContent, GameLocalConfig, HistoryBoardConfig } from './types'
import { isPlaceholderResolved, buildPlaceholderContent } from './_placeholder'

// re-export 給既有 consumer（HistoryDetail / useGameId / 其他元件 import from registry）
export type { GameContent, GameHistoryContent, GameLocalConfig, HistoryBoardConfig }

// ============================================================================
// 型別
// ============================================================================

/** 三種 native help modal 的 key */
export type HelpType = 'paytable' | 'rules' | 'history'

// ============================================================================
// 共用 help modal —— **lazy**（2026-07-29 bundle 拆分）
// ============================================================================

/**
 * 🛑 共用 modal **不可改回靜態 import**。registry 是 entry 必載（REGISTERED_GAME_IDS），
 * 靜態 import 會把 PaytableModal / HistoryDetailModal → HistoryDetail → WinSection →
 * useWinSection 整條鏈綁進 entry —— 玩家沒開過彈窗也得先下載全部結算 UI。
 *
 * 載入模型與 per-game 模組一致：`ensureGameLoaded()` 併行載入（placeholder / 未註冊
 * gameId 也載，那條路一樣要開共用空殼 modal），resolver 照舊**同步**讀 reactive cache。
 * 正常時序（fetchGameCode / replay 入口都有 await）下，consumer 讀到的一定是已載入；
 * 載入前讀到 undefined 時，cache 是 shallowReactive → computed 會在載完後自動重算
 * （與 customModal 同一份契約）。
 */
type SharedModalKey = HelpType | 'board'

const SHARED_MODAL_LOADERS: Record<SharedModalKey, () => Promise<{ default: Component }>> = {
  paytable: () => import('@/components/native-help/PaytableModal.vue'),
  rules: () => import('@/components/native-help/RulesModal.vue'),
  history: () => import('@/components/native-help/HistoryDetailModal.vue'),
  board: () => import('@/components/native-help/BoardHistoryDetailModal.vue'),
}

const SHARED_MODAL_KEYS = Object.keys(SHARED_MODAL_LOADERS) as SharedModalKey[]

/** 已載入的共用 modal（shallowReactive：只需「key 出現了」這一層反應性） */
const sharedModals = shallowReactive({} as Partial<Record<SharedModalKey, Component>>)

let sharedInflight: Promise<void> | null = null

/** 載入四個共用 help modal（冪等、single-flight）。由 ensureGameLoaded 帶動。 */
/**
 * 單一載入 job 的包裝：**吞掉失敗、只記 warn**，讓 `ensureSharedHelpLoaded` /
 * `ensureGameLoaded` 永遠 resolve、永遠不 reject。
 *
 * 🛑 **不要拿掉這層 catch。** 舊版是 `Promise.all(jobs)` 直接往上拋，而
 *    `useGameId.fetchGameCode` 與 `App.vue` 的 `initPage()` 兩邊都沒有 catch ——
 *    任何一個 chunk 載入失敗（手機弱網掉一次 request、或玩家手上的 index.html
 *    指到已被換掉的 chunk hash）就會變成 **unhandled rejection**：`pageError` 不會被設、
 *    `authReady` / `gameLoaded` 永遠不轉 true → `showLoading` 恆為 true，
 *    玩家卡在**無限 loading**，看不到錯誤畫面也沒有重試（release build 連 console 都被 drop）。
 *
 * 改成「失敗也 resolve」之後，下游的同步 resolver 讀不到 cache →
 * `fetchGameCode` 既有的「content 載入失敗」分支會正常觸發 → PageErrorScreen。
 * 失敗的項目**不寫 cache**，所以 inflight 清掉後下次呼叫會自動重試。
 */
const settle = (label: string, job: Promise<void>): Promise<void> =>
  job.catch((e: unknown) => {
    console.warn(`[web][registry] ${label} 載入失敗（不中斷開機流程）:`, e)
  })

export function ensureSharedHelpLoaded(): Promise<void> {
  if (SHARED_MODAL_KEYS.every((k) => sharedModals[k])) return Promise.resolve()
  if (!sharedInflight) {
    sharedInflight = Promise.all(
      SHARED_MODAL_KEYS.map((k) =>
        settle(
          `shared modal "${k}"`,
          SHARED_MODAL_LOADERS[k]().then((mod) => {
            sharedModals[k] = mod.default
          }),
        ),
      ),
    )
      .then(() => {})
      .finally(() => {
        sharedInflight = null
      })
  }
  return sharedInflight
}

// ============================================================================
// Glob 掃描（build-time 靜態展開；value 是 lazy loader，不呼叫就不進 bundle）
// ============================================================================

type ModuleLoader = () => Promise<unknown>

const CONTENT_GLOB = import.meta.glob('./*/content.ts') as Record<string, ModuleLoader>
const HISTORY_BOARD_GLOB = import.meta.glob('./*/historyBoard.ts') as Record<string, ModuleLoader>
const CUSTOM_MODAL_GLOB = import.meta.glob('./*/components/*HistoryDetailModal.vue') as Record<
  string,
  ModuleLoader
>

/** './sgcl/content.ts' → 'sgcl' */
const idFromPath = (path: string): string => path.split('/')[1] ?? ''

const byId = (glob: Record<string, ModuleLoader>): Record<string, ModuleLoader> => {
  const out: Record<string, ModuleLoader> = {}
  for (const [path, loader] of Object.entries(glob)) out[idFromPath(path)] = loader
  return out
}

const CONTENT_LOADERS = byId(CONTENT_GLOB)
const HISTORY_BOARD_LOADERS = byId(HISTORY_BOARD_GLOB)
const CUSTOM_MODAL_LOADERS = byId(CUSTOM_MODAL_GLOB)

/**
 * 所有已註冊 gameId（= 存在 `src/games/<id>/content.ts`）。
 * build-time 靜態可知，**不觸發任何 import**。
 * nativeHelpGames.ts 的 NATIVE_HELP_GAMES 白名單由此派生。
 */
export const REGISTERED_GAME_IDS: ReadonlySet<string> = new Set(Object.keys(CONTENT_LOADERS))

// ============================================================================
// Lazy load + reactive cache
// ============================================================================

/** 依約定從模組挑 named export（`<UPPER>_CONTENT` / `<UPPER>_HISTORY_BOARD`） */
const pickExport = <T>(mod: unknown, suffix: string): T | undefined => {
  const record = mod as Record<string, unknown>
  const key = Object.keys(record).find((k) => k.endsWith(suffix))
  return key ? (record[key] as T) : undefined
}

/**
 * 已載入的 per-game 模組 cache。
 * 每張表都是 reactive → resolver 在 computed / template 內被呼叫時，load 完成會自動觸發重算
 * （開機序：fetchGameCode await ensureGameLoaded 之後才 mount 遊戲 UI，正常時序下
 *  consumer 讀到的都是已載入狀態；reactive 是「先跑到的 computed」的保險）。
 *
 * 🛑 **必須是 `shallowReactive`，不可用 `reactive`**。
 * `reactive` 會**深層代理**存進去的值 —— 裡面裝的是 Vue 元件（`customModal`）和內含
 * 元件的設定（`historyBoard.board`），被代理後 `<component :is>` 會噴：
 *   「Vue received a Component that was made a reactive object.」
 * 而且深層代理整份 `content`（賠付表 / 規則 section、符號陣列）也是純粹的效能浪費。
 * `shallowReactive` 只追蹤「key 有沒有被加進來」——正是我們需要的唯一反應性，
 * 值則原樣返回、不包 proxy。
 */
const loaded = {
  content: shallowReactive({} as Record<string, GameContent>),
  historyBoard: shallowReactive({} as Record<string, HistoryBoardConfig>),
  customModal: shallowReactive({} as Record<string, Component>),
}

const inflight = new Map<string, Promise<void>>()

/**
 * 載入該 gameId 的 content（+ 存在的話 historyBoard / 自訂 modal），寫進 cache。
 * - 冪等：已載入 / 載入中都安全重入。
 * - 未註冊 gameId → no-op resolve（placeholder 流程照舊）。
 * - 呼叫點：useGameId.fetchGameCode（正常入口）、replay 入口 onMounted。
 */
export function ensureGameLoaded(gameId: string | null | undefined): Promise<void> {
  const id = gameId?.toLowerCase()
  if (!id) return Promise.resolve()
  // 共用 modal 與 per-game 模組併行載。**未註冊 / placeholder 的 gameId 也要**——
  // 那條路開的三個 help modal 用的就是共用空殼。
  const shared = ensureSharedHelpLoaded()
  const contentLoader = CONTENT_LOADERS[id]
  if (!contentLoader || loaded.content[id]) return shared

  let pending = inflight.get(id)
  if (!pending) {
    // 每個 job 都過 settle()：單一 chunk 失敗只會少一塊 cache，不會炸掉整條開機鏈。
    const jobs: Promise<void>[] = [
      shared,
      settle(
        `${id}/content.ts`,
        contentLoader().then((mod) => {
          const content = pickExport<GameContent>(mod, '_CONTENT')
          if (!content) throw new Error(`[registry] ${id}/content.ts 缺少 *_CONTENT export`)
          loaded.content[id] = content
        }),
      ),
    ]
    const hbLoader = HISTORY_BOARD_LOADERS[id]
    if (hbLoader) {
      jobs.push(
        settle(
          `${id}/historyBoard.ts`,
          hbLoader().then((mod) => {
            const config = pickExport<HistoryBoardConfig>(mod, '_HISTORY_BOARD')
            if (!config)
              throw new Error(`[registry] ${id}/historyBoard.ts 缺少 *_HISTORY_BOARD export`)
            loaded.historyBoard[id] = config
          }),
        ),
      )
    }
    const modalLoader = CUSTOM_MODAL_LOADERS[id]
    if (modalLoader) {
      jobs.push(
        settle(
          `${id} 自訂 history modal`,
          modalLoader().then((mod) => {
            loaded.customModal[id] = (mod as { default: Component }).default
          }),
        ),
      )
    }
    pending = Promise.all(jobs)
      .then(() => {})
      .finally(() => inflight.delete(id))
    inflight.set(id, pending)
  }
  return pending
}

const getContent = (gameId: string | null | undefined): GameContent | undefined => {
  if (!gameId) return undefined
  const found = loaded.content[gameId.toLowerCase()]
  if (found) return found
  // 未建立的遊戲：fetchGameCode 在後台 API 也查無後會 markPlaceholderResolved，
  // 此時回合成空殼 content（gameUrl 用 gameCode 湊）。詳見 ./_placeholder.ts
  if (isPlaceholderResolved(gameId)) return buildPlaceholderContent(gameId)
  return undefined
}

// ============================================================================
// Help (paytable / rules / history modal) resolvers
// ============================================================================

/**
 * 取要 render 的 modal 元件。
 *   - paytable / rules → 共用元件（content.ts 資料驅動）
 *   - history → 自訂 modal ＞ historyBoard（共用 BoardHistoryDetailModal）＞ 共用 HistoryDetailModal
 *   - 未註冊 → placeholder 已解析時給共用空殼，否則 undefined（useModal 會 fallback）
 *
 * ⚠️ 自訂 modal 讀 lazy cache：正常時序（ensureGameLoaded 之後）一定拿得到；
 *    載入前呼叫會回 undefined，cache reactive 會讓 computed 在載完後自動重算。
 */
export const resolveNativeHelpComponent = (
  gameId: string | null | undefined,
  modalKey: string,
): Component | undefined => {
  if (!gameId) return undefined
  const id = gameId.toLowerCase()

  if (!REGISTERED_GAME_IDS.has(id)) {
    // 未註冊但已解析為 placeholder → 三個 help key 全走共用空殼 modal
    if (
      isPlaceholderResolved(gameId) &&
      (modalKey === 'paytable' || modalKey === 'rules' || modalKey === 'history')
    ) {
      return sharedModals[modalKey] // 載入前 undefined（reactive，載完自動重算）
    }
    return undefined
  }

  if (modalKey === 'paytable' || modalKey === 'rules') return sharedModals[modalKey]
  if (modalKey !== 'history') return undefined

  if (CUSTOM_MODAL_LOADERS[id]) return loaded.customModal[id] // 載入前 undefined（見上方 doc）
  if (HISTORY_BOARD_LOADERS[id]) return sharedModals.board
  return sharedModals.history
}

/** BoardHistoryDetailModal 用：拿該遊戲的 HistoryBoardConfig（非 historyBoard 註冊 → undefined） */
export const resolveHistoryBoardConfig = (
  gameId: string | null | undefined,
): HistoryBoardConfig | undefined => {
  if (!gameId) return undefined
  return loaded.historyBoard[gameId.toLowerCase()]
}

// ============================================================================
// Content resolvers
// ============================================================================

/**
 * 從 content.identity / chrome / balance 拼出 GameLocalConfig
 * （向下相容 useGameId / App.vue 既有消費端）
 */
export const resolveGameLocalConfig = (
  gameId: string | null | undefined,
): GameLocalConfig | undefined => {
  const content = getContent(gameId)
  if (!content) return undefined

  return {
    key: content.identity.key,
    gameCode: content.identity.gameCode,
    gameUrl: content.identity.gameUrl,
    gameName: content.identity.gameName,
    assets: {
      background: content.chrome?.background,
      walletIcon: content.balance?.walletIcon,
      tintWalletIcon: content.balance?.tintWalletIcon,
      walletLabel: content.balance?.walletLabel?.zh,
      walletLabelEn: content.balance?.walletLabel?.en,
      transitionTextColor: content.chrome?.transitionTextColor,
      themeColor: content.chrome?.themeColor,
      formulaColor: content.chrome?.formulaColor,
      currencies: content.balance?.currencies,
    },
  }
}

/** 共用 modal 元件用：拿整份 content 物件 */
export const resolveGameContent = (gameId: string | null | undefined): GameContent | undefined => {
  return getContent(gameId)
}

/** HistoryDetail / HistoryBoard 元件用：只拿 history 段 */
export const resolveHistoryContent = (
  gameId: string | null | undefined,
): GameHistoryContent | undefined => {
  return getContent(gameId)?.history
}
